/**
 * HelpRecents — section « Récents » BICÉPHALE (HELP-DRAWER lot §1.1 + §5).
 *
 * Une seule liste, deux types d'entrées, distinguées par leur icône :
 *   - recherche (icône horloge) → se rejoue (re-remplit + re-filtre) ;
 *   - conversation IA (icône bulle) → rouvre le fil stocké TEL QUEL, sans régénérer.
 *
 * §1.1 : pas d'icône sur l'en-tête (le libellé suffit) ; l'icône vit sur les lignes.
 */

import { useTranslation } from 'react-i18next'
import { History, MessageCircle, X } from 'lucide-react'

import type { RecentEntry } from '../../lib/helpSearchHistory'
import { recentEntryKey } from '../../lib/helpSearchHistory'

export function HelpRecents({
    entries,
    onReplaySearch,
    onReopenConversation,
    onRemove,
    onClear,
}: {
    entries: RecentEntry[]
    onReplaySearch: (query: string) => void
    onReopenConversation: (entry: Extract<RecentEntry, { kind: 'conversation' }>) => void
    onRemove: (entry: RecentEntry) => void
    onClear: () => void
}) {
    const { t } = useTranslation()
    if (entries.length === 0) return null

    return (
        <div className="rounded-xl bg-card p-3 shadow-md">
            <div className="mb-2 flex items-center justify-between">
                {/* §1.1 : pas d'icône sur l'en-tête. */}
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('help.recent.title')}
                </span>
                <button
                    type="button"
                    onClick={onClear}
                    className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    {t('help.recent.clearAll')}
                </button>
            </div>
            <ul className="space-y-1">
                {entries.map((entry) => {
                    const isConversation = entry.kind === 'conversation'
                    const label = isConversation ? entry.title || t('help.recent.conversation') : entry.query
                    const Icon = isConversation ? MessageCircle : History
                    return (
                        <li
                            key={recentEntryKey(entry)}
                            className="group flex items-center gap-1 rounded-lg transition-colors hover:bg-muted/60 motion-reduce:transition-none"
                        >
                            <button
                                type="button"
                                onClick={() =>
                                    isConversation
                                        ? onReopenConversation(entry)
                                        : onReplaySearch(entry.query)
                                }
                                aria-label={
                                    isConversation
                                        ? t('help.recent.reopenConversation', { title: label })
                                        : t('help.recent.replaySearch', { query: label })
                                }
                                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <Icon
                                    className={
                                        isConversation
                                            ? 'h-3.5 w-3.5 shrink-0 text-ai'
                                            : 'h-3.5 w-3.5 shrink-0 text-muted-foreground'
                                    }
                                />
                                <span className="truncate">{label}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => onRemove(entry)}
                                aria-label={t('help.recent.remove', { query: label })}
                                className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group-hover:opacity-100 motion-reduce:transition-none"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
